/**
 * Schlanker GPS-Status-Indicator (Cluster 13.1 aus Probst-Besprechung 2026-06-25).
 *
 * Ersetzt die grossen GPS-Status-Bloecke im Zeiterfassung-Stempel und im
 * BauberichteV2-Aufgaben-Timer durch eine einzelne inline Pill:
 * Icon + 1-Wort-Status, 28 px hoch, klickbar. Der Klick oeffnet einen
 * Details-Popover mit vollstaendigem Text + Toggle-Button.
 *
 * Der Indicator ist rein visuell — State (aktiv/aus/pflicht) und
 * Toggle-Handler kommen vom Aufrufer, weil die Persistenz je nach
 * Kontext unterschiedlich ist (Zeiterfassung: `zeiterfassung.gpsEnabled`,
 * BV2: `bauberichteV2.gpsEnabled`).
 */
import { useEffect, useRef, useState } from 'react';
import { MapPin, MapPinOff } from 'lucide-react';

export interface GpsStatusIndicatorProps {
  /** true = GPS wird gesendet (aktiv). false = kein Standort. */
  active: boolean;
  /** true = Admin-Policy zwingt GPS an. Toggle wird ausgeblendet. */
  required?: boolean;
  /** Text im Details-Popover — Kontext (Stempel vs Aufgaben-Timer). */
  detailText: string;
  /** Toggle-Callback. undefined wenn required=true. */
  onToggle?: () => void;
  /** Optional: label-Override (Default "GPS"). */
  label?: string;
}

export default function GpsStatusIndicator({
  active, required = false, detailText, onToggle, label = 'GPS',
}: GpsStatusIndicatorProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    const t = setTimeout(() => document.addEventListener('click', onClickOutside), 0);
    return () => { clearTimeout(t); document.removeEventListener('click', onClickOutside); };
  }, [open]);

  const statusWord = required ? 'Pflicht' : active ? 'aktiv' : 'aus';
  const Icon = active ? MapPin : MapPinOff;
  const pillClasses = required
    ? 'bg-amber-500/15 border-amber-500/40 text-amber-200'
    : active
      ? 'bg-brand-500/15 border-brand-500/40 text-brand-200'
      : 'bg-slate-800 border-slate-700 text-slate-400';

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={detailText}
        aria-label={`${label} ${statusWord}`}
        className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-xs font-medium transition-colors hover:brightness-110 ${pillClasses}`}
      >
        <Icon size={12} className="shrink-0" />
        <span>{label} {statusWord}</span>
      </button>
      {open && (
        <div
          role="dialog"
          className="absolute right-0 top-full mt-1 z-30 min-w-[16rem] max-w-[20rem] rounded-lg border border-slate-700 bg-slate-900 shadow-xl p-3 text-xs text-slate-300"
        >
          <div className="mb-2 flex items-start gap-2">
            <Icon size={14} className={active ? 'text-brand-400 mt-0.5' : 'text-slate-500 mt-0.5'} />
            <div className="min-w-0">
              <div className="font-medium text-slate-100 mb-0.5">{label} {statusWord}</div>
              <div className="text-slate-400 leading-snug">{detailText}</div>
            </div>
          </div>
          {onToggle && !required && (
            <button
              type="button"
              onClick={() => { onToggle(); setOpen(false); }}
              className={`w-full mt-1 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                active
                  ? 'bg-slate-700 hover:bg-slate-600 text-slate-100'
                  : 'bg-brand-600 hover:bg-brand-500 text-primary-foreground'
              }`}
            >
              {active ? `${label} ausschalten` : `${label} einschalten`}
            </button>
          )}
          {required && (
            <p className="mt-1 text-[11px] text-amber-300/80 italic">
              Vom Admin vorgegeben — nicht abschaltbar.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
